import { newId, type Aperture, type Item, type OpeningKind } from "./types";

export type { Aperture };

export interface SnapSeg {
  ax: number;
  az: number;
  bx: number;
  bz: number;
  itemId: string;
}

export const SNAP_DIST = 0.75;

export function wallEnds(item: Item): { ax: number; az: number; bx: number; bz: number } {
  const r = item.rotation;
  const c = Math.cos(r);
  const s = Math.sin(r);
  const half = item.width / 2;
  return {
    ax: item.x - half * c,
    az: item.z - half * s,
    bx: item.x + half * c,
    bz: item.z + half * s,
  };
}

export function setWallFromEnds(item: Item, ax: number, az: number, bx: number, bz: number): void {
  const dx = bx - ax;
  const dz = bz - az;
  const len = Math.hypot(dx, dz);
  item.width = Math.max(0.4, Math.round(len * 100) / 100);
  item.rotation = Math.atan2(dz, dx);
  item.x = (ax + bx) / 2;
  item.z = (az + bz) / 2;
  for (const ap of wallApertures(item)) clampAperture(item, ap);
}

export function wallApertures(item: Item): Aperture[] {
  if (item.apertures?.length) return item.apertures;
  if (item.hasDoor) {
    return [
      {
        id: item.id + "-door",
        kind: "door",
        offset: item.doorOffset ?? 1.2,
        width: item.doorWidth ?? 0.8,
        height: item.doorHeight ?? 2.1,
        sill: item.doorSill ?? 0,
      },
    ];
  }
  return [];
}

export function ensureApertures(item: Item): Aperture[] {
  if (!item.apertures) {
    item.apertures = wallApertures(item).map((ap) => ({ ...ap, id: newId() }));
  }
  return item.apertures;
}

export function addAperture(item: Item, kind: OpeningKind, offset: number): Aperture {
  const aps = ensureApertures(item);
  const ap: Aperture = {
    id: newId(),
    kind,
    offset,
    width: kind === "door" ? 0.8 : 1.2,
    height: kind === "door" ? 2.1 : 1.2,
    sill: kind === "door" ? 0 : 1.05,
  };
  clampAperture(item, ap);
  aps.push(ap);
  item.hasDoor = false;
  return ap;
}

export function clampAperture(item: Item, ap: Aperture): void {
  ap.width = Math.min(Math.max(ap.width, 0.4), Math.max(0.5, item.width - 0.2));
  const max = Math.max(0.05, item.width - ap.width - 0.05);
  ap.offset = Math.min(max, Math.max(0.05, Math.round(ap.offset * 100) / 100));
  ap.height = Math.min(Math.max(ap.height, 0.4), Math.max(0.5, item.height - 0.08));
  ap.sill = Math.min(Math.max(0, ap.sill), Math.max(0, item.height - ap.height - 0.05));
}

export function offsetAlongWall(item: Item, x: number, z: number): number {
  const e = wallEnds(item);
  const dx = e.bx - e.ax;
  const dz = e.bz - e.az;
  const len2 = dx * dx + dz * dz;
  if (len2 < 1e-8) return 0;
  const t = ((x - e.ax) * dx + (z - e.az) * dz) / len2;
  return t * item.width;
}

export function clampDoor(item: Item): void {
  for (const ap of ensureApertures(item)) clampAperture(item, ap);
}

export function collectSnapSegs(items: Item[], excludeId?: string): SnapSeg[] {
  const segs: SnapSeg[] = [];
  for (const it of items) {
    if (it.id === excludeId) continue;
    if (it.kind === "wall") {
      const e = wallEnds(it);
      segs.push({ ax: e.ax, az: e.az, bx: e.bx, bz: e.bz, itemId: it.id });
    }
    if (it.kind === "room" || it.kind === "house") {
      const r = it.rotation;
      const c = Math.cos(r);
      const s = Math.sin(r);
      const hw = it.width / 2;
      const hd = it.depth / 2;
      const corners = [
        [-hw, -hd],
        [hw, -hd],
        [hw, hd],
        [-hw, hd],
      ].map(([lx, lz]) => ({
        x: it.x + lx * c - lz * s,
        z: it.z + lx * s + lz * c,
      }));
      for (let i = 0; i < 4; i++) {
        const a = corners[i];
        const b = corners[(i + 1) % 4];
        segs.push({ ax: a.x, az: a.z, bx: b.x, bz: b.z, itemId: it.id });
      }
    }
  }
  return segs;
}

export function snapPoint(
  x: number,
  z: number,
  segs: SnapSeg[],
  maxDist = SNAP_DIST,
): { x: number; z: number; itemId: string } | null {
  let best: { x: number; z: number; itemId: string } | null = null;
  let bestD = maxDist;
  for (const seg of segs) {
    for (const p of [
      closestOnSeg(x, z, seg),
      { x: seg.ax, z: seg.az },
      { x: seg.bx, z: seg.bz },
    ]) {
      const d = Math.hypot(p.x - x, p.z - z);
      if (d < bestD) {
        bestD = d;
        best = { x: p.x, z: p.z, itemId: seg.itemId };
      }
    }
  }
  return best;
}

export function snapWorld(
  x: number,
  z: number,
  items: Item[],
  excludeId?: string,
  preferIds?: string[],
): { x: number; z: number } {
  const segs = collectSnapSegs(items, excludeId);
  const preferred = preferIds?.length ? segs.filter((s) => preferIds.includes(s.itemId)) : [];
  const hit =
    (preferred.length ? snapPoint(x, z, preferred, 80) : null) ?? snapPoint(x, z, segs);
  if (hit) return hit;
  const host = containingBox(x, z, items, excludeId);
  if (host) {
    const inside = snapPoint(x, z, collectSnapSegs([host]), 80);
    if (inside) return inside;
  }
  return { x, z };
}

function containingBox(x: number, z: number, items: Item[], excludeId?: string): Item | null {
  let host: Item | null = null;
  let area = Infinity;
  for (const it of items) {
    if (it.id === excludeId) continue;
    if (it.kind !== "room" && it.kind !== "house") continue;
    if (!pointInItem(x, z, it)) continue;
    const a = it.width * it.depth;
    if (a < area) {
      area = a;
      host = it;
    }
  }
  return host;
}

function pointInItem(x: number, z: number, it: Item): boolean {
  const dx = x - it.x;
  const dz = z - it.z;
  const c = Math.cos(-it.rotation);
  const s = Math.sin(-it.rotation);
  const lx = dx * c - dz * s;
  const lz = dx * s + dz * c;
  return Math.abs(lx) <= it.width / 2 + 0.05 && Math.abs(lz) <= it.depth / 2 + 0.05;
}

function closestOnSeg(x: number, z: number, seg: SnapSeg): { x: number; z: number } {
  const dx = seg.bx - seg.ax;
  const dz = seg.bz - seg.az;
  const len2 = dx * dx + dz * dz;
  if (len2 < 1e-8) return { x: seg.ax, z: seg.az };
  const t = Math.min(1, Math.max(0, ((x - seg.ax) * dx + (z - seg.az) * dz) / len2));
  return { x: seg.ax + dx * t, z: seg.az + dz * t };
}
